import {track} from '@getmoss/moss-analytics'
import {useSearchParams} from 'next/navigation'
import {useRouter} from 'next/router'
import {useEffect, useMemo, useState} from 'react'
import {useCookie} from 'react-use'
import {DEVICE_ID_COOKIE_NAME, HUBSPOT_LOCALE_FORM_MAP} from 'src/common/constants'
import {getDeviceInfo} from 'src/common/helpers/deviceInfo'
import type {FieldError} from 'src/common/helpers/findFieldError'
import {useFunnelConsents} from 'src/common/hooks/useFunnelConsents'
import {useGetDomainCountry} from 'src/common/hooks/useGetDomainCountry'
import {useGetExperimentParameters} from 'src/common/hooks/useGetExperimentParameters'
import {useGetOriginPageParam} from 'src/common/hooks/useGetPricingFormHandler/useGetPricingFormHandler'
import {useHubspotTracking} from 'src/common/hooks/useHubspotTracking'
import {useMossApi} from 'src/common/hooks/useMossApi'
import {useRecaptcha} from 'src/common/hooks/useRecaptcha'
import type {HubspotFormLocale} from 'src/common/types'
import {useLeadIdContext} from '../context/LeadIdContext'

type UseLeadSubmissionProps = {
  pageUid?: string
  errorMessage?: string
  onSuccess?: () => void
  onError?: (message: string) => void
  tracking: {
    recaptchaAction: string
    formName: string
    intent: string
    location: string
  }
  /**
   * When true, uses the singleton recaptcha loader (new approach).
   * When false, expects executeRecaptchaFromWrapper prop (old RecaptchaWrapper approach).
   * @default true
   */
  useSingletonRecaptcha?: boolean
  /**
   * Execute function from GoogleReCaptchaProvider (react-google-recaptcha-v3).
   * Only used when useSingletonRecaptcha is false.
   */
  executeRecaptchaFromWrapper?: ((action: string) => Promise<string>) | null
}

type ApiError = {
  response?: {
    data?: {
      fieldErrors?: FieldError[]
    }
  }
  message?: string
}

// A rejected promise can throw anything (a string, null, a plain object), not just an ApiError, so
// this guards the shape before it's read instead of trusting an unconditional cast.
const isApiError = (value: unknown): value is ApiError => typeof value === 'object' && value !== null

export const useLeadSubmission = ({
  pageUid,
  errorMessage,
  onSuccess,
  onError,
  tracking,
  useSingletonRecaptcha = true,
  executeRecaptchaFromWrapper,
}: UseLeadSubmissionProps) => {
  const [apiError, setApiError] = useState<ApiError | null>(null)
  const domainCountry = useGetDomainCountry()
  const {execute: executeRecaptchaSingleton} = useRecaptcha()
  const router = useRouter()
  const locale = router.locale?.split('-')[0] as HubspotFormLocale
  const {dppDocId, marketingDocId, dppURL, marketingURL} = useFunnelConsents(locale)
  const [deviceId] = useCookie(DEVICE_ID_COOKIE_NAME)
  const {trackHubspot} = useHubspotTracking(HUBSPOT_LOCALE_FORM_MAP[locale] || HUBSPOT_LOCALE_FORM_MAP['en'])
  const expParams = useGetExperimentParameters()
  const originPage = useGetOriginPageParam()
  const params = useSearchParams()
  const funnelEntrySource = params.get('src') || 'NAVIGATION'
  const {setLeadId} = useLeadIdContext()

  const headers = useMemo(() => {
    const expHeader = expParams.map((param) => `${param.name}:${param.value}`).join(',')
    return {
      'Moss-Site-Experiments': expHeader,
    }
  }, [expParams])

  const {mossApi} = useMossApi({
    headers,
  })

  const trackingPageData = {
    type: pageUid || '',
    domainCountry,
  }

  // Preload reCAPTCHA script on mount to avoid race condition during submission
  // Only when using singleton approach (not needed for RecaptchaWrapper)
  useEffect(() => {
    if (useSingletonRecaptcha) {
      executeRecaptchaSingleton(tracking.recaptchaAction).catch(() => {
        // Silently ignore errors during preload - the script will be retried during actual submission
      })
    }
  }, [useSingletonRecaptcha, executeRecaptchaSingleton, tracking.recaptchaAction])

  const submitLead = async (email: string, captchaTokenV2?: string) => {
    // Use the appropriate execute function based on the toggle
    const executeRecaptcha = useSingletonRecaptcha ? executeRecaptchaSingleton : executeRecaptchaFromWrapper

    const captchaTokenV3 = executeRecaptcha ? await executeRecaptcha(tracking.recaptchaAction) : null

    if (!captchaTokenV3) {
      track({
        event: 'interaction',
        interaction: {
          element: 'recaptcha-failed',
          location: tracking.location,
          type: 'error',
        },
        page: trackingPageData,
      })
    }

    try {
      let captchaToken: string = ''

      if (captchaTokenV2) {
        captchaToken = captchaTokenV2
      } else if (captchaTokenV3) {
        captchaToken = captchaTokenV3
      }

      const response = await mossApi.leadController.create(
        {
          funnelEntrySource,
        },
        {
          email,
          captchaToken,
          originPage,
          marketingConsentDocId: marketingDocId,
          marketingConsentUrl: marketingURL,
          intent: 'NEUTRAL',
          privacyPolicyDocId: dppDocId,
          privacyPolicyUrl: dppURL,
          metadata: {
            referrer: document.referrer,
            ...Object.fromEntries(params.entries()),
            ...getDeviceInfo(),
          },
          acceptedLegalConsentTypes: ['LEGAL_CENTER', 'MARKETING'],
          deviceId: deviceId || undefined,
        },
      )

      if (response?.data.leadId) {
        setLeadId(response.data.leadId)

        track({
          event: 'form_submission',
          form: {
            formName: tracking.formName,
            funnelId: response.data.leadId,
            intent: tracking.intent,
            stepName: 'lead_funnel_start_page_viewed',
          },
          page: trackingPageData,
        })

        trackHubspot({
          fields: [
            {
              name: 'email',
              value: email,
            },
          ],
        })

        onSuccess?.()
      }
    } catch (error: unknown) {
      const caughtError: ApiError = isApiError(error) ? error : {message: String(error)}

      if ((caughtError.response?.data?.fieldErrors?.length ?? 0) > 0) {
        onError?.(errorMessage || 'Invalid email')
      }

      setApiError(caughtError)

      track({
        event: 'interaction',
        interaction: {
          element: 'request-failed',
          location: tracking.location,
          type: 'error',
          detail: caughtError.message || 'Unknown error',
        },
        page: trackingPageData,
      })
    }
  }

  return {submitLead, apiError}
}
